import {
  fetchStaticAction,
  type FetchStaticParamsType,
} from "@/utils/serverActions/static/staticActions";

import { callQuery, QueryHttpError } from "./callQuery";

type StaticResult = Awaited<ReturnType<typeof callQuery<"static">>>;

type Batch = {
  languageCode: string;
  sections: Set<string>;
  // What the `states` section asks for, or null when the batch has no such section:
  // a country id, or "all" for every state. A batch holds one answer only.
  statesMode: number | "all" | null;
  waiters: { resolve: (data: StaticResult) => void; reject: (error: unknown) => void }[];
};

// Calls made within this window share one request. React Query starts the catalogs
// of a screen in the same commit, so a few milliseconds are enough to gather them.
const BATCH_WINDOW_MS = 10;

let open: Batch[] = [];

function statesModeOf(params: FetchStaticParamsType): number | "all" | null {
  if (!params.p_sections.includes("states")) return null;
  return params.p_country_id ?? "all";
}

function flush(batch: Batch) {
  open = open.filter((b) => b !== batch);
  const statesCountry = typeof batch.statesMode === "number" ? batch.statesMode : undefined;
  const params: FetchStaticParamsType = {
    p_language_code: batch.languageCode,
    p_sections: [...batch.sections],
    ...(statesCountry !== undefined ? { p_country_id: statesCountry } : {}),
  };
  callQuery("static", params)
    .catch((error: unknown) => {
      // /api requires a web session (proxy.ts). The public pages (candidate portal,
      // customer form) have none, so they read the catalogs through the Server Action.
      if (error instanceof QueryHttpError && error.status === 401) {
        return fetchStaticAction(params);
      }
      throw error;
    })
    .then(
    (data) => batch.waiters.forEach((w) => w.resolve(data)),
    (error) => batch.waiters.forEach((w) => w.reject(error)),
  );
}

/**
 * fn_static takes a list of sections, but each catalog hook asks for its own, so a
 * screen sent one request per catalog (four in the people filter). This gathers the
 * calls of the same language made at about the same time into one request. Each
 * caller gets the whole answer and reads its own section, as before.
 */
export function fetchStaticBatched(params: FetchStaticParamsType): Promise<StaticResult> {
  const mode = statesModeOf(params);
  let batch = open.find(
    (b) =>
      b.languageCode === params.p_language_code &&
      (mode === null || b.statesMode === null || b.statesMode === mode),
  );
  if (!batch) {
    batch = { languageCode: params.p_language_code, sections: new Set(), statesMode: null, waiters: [] };
    open.push(batch);
    const created = batch;
    setTimeout(() => flush(created), BATCH_WINDOW_MS);
  }
  params.p_sections.forEach((s) => batch.sections.add(s));
  if (mode !== null) batch.statesMode = mode;

  const target = batch;
  return new Promise<StaticResult>((resolve, reject) => {
    target.waiters.push({ resolve, reject });
  });
}
